import React from "react";
import { createPortal } from "react-dom";
import { X, Zap, Building2, BarChart3, Infinity, Search, FolderOpen, TrendingUp, GraduationCap } from "lucide-react";

interface PlanUpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpgrade: () => void;
  currentUsage?: {
    essays: number;
    maxEssays: number;
    analyses: number;
    maxAnalyses: number;
  };
}

const PlanUpgradeModal: React.FC<PlanUpgradeModalProps> = ({
  open,
  onOpenChange,
  onUpgrade,
  currentUsage = { essays: 0, maxEssays: 5, analyses: 0, maxAnalyses: 5 },
}) => {
  if (!open) return null;

  const paidFeatures = [
    {
      icon: Zap,
      title: "Priority support",
      description: "Get dedicated assistance within hours, not days.",
    },
    {
      icon: Building2,
      title: "University matching",
      description: "Connect with verified alumni from top schools.",
    },
    {
      icon: BarChart3,
      title: "Advanced analytics",
      description: "Track your essay scores and identify patterns.",
    },
    {
      icon: Infinity,
      title: "Unlimited essays",
      description: "Write and revise as many essays as you need.",
    },
    {
      icon: Search,
      title: "College searching",
      description: "Find colleges that best fit your profile and goals.",
    },
    {
      icon: FolderOpen,
      title: "Advanced organization",
      description: "Organize essays by school, deadline, or status.",
    },
    {
      icon: TrendingUp,
      title: "Progress tracking",
      description: "Visualize your writing journey with progress charts.",
    },
    {
      icon: GraduationCap,
      title: "Scholarship directory",
      description: "Access a directory of over 30,000 scholarships.",
    },
  ];

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient background */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4 sm:p-6 rounded-t-2xl relative overflow-hidden">
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '24px 24px'
          }} />
          
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
          
          <div className="relative flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-white">
                Be prepared for the world's most competitive colleges
              </h2>
              <p className="text-sm text-gray-300 mt-0.5">
                Upgrade to Pro to match with Ivy League Alums
              </p>
            </div>
          </div>
          
          {/* Usage stats in header */}
          <div className="relative mt-4 grid grid-cols-2 gap-3">
            <div className="bg-white/10 backdrop-blur rounded-xl p-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-gray-300">Essays Used</span>
                <span className="text-xs font-medium text-white">
                  {currentUsage.essays}/{currentUsage.maxEssays}
                </span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-1.5">
                <div
                  className="bg-white h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(
                      (currentUsage.essays / currentUsage.maxEssays) * 100,
                      100
                    )}%`,
                  }}
                />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-gray-300">Analyses Left</span>
                <span className="text-xs font-medium text-white">
                  {currentUsage.maxAnalyses - currentUsage.analyses}/{currentUsage.maxAnalyses}
                </span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-1.5">
                <div
                  className="bg-white h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(
                      ((currentUsage.maxAnalyses - currentUsage.analyses) / currentUsage.maxAnalyses) * 100,
                      100
                    )}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Features grid */}
        <div className="p-4 sm:p-5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">What you'll get with Pro</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {paidFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="flex items-start gap-3 p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100">
                    <IconComponent className="w-4 h-4 text-gray-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-gray-900 text-sm leading-tight">
                      {feature.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-5 py-3 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
          <div className="flex flex-col-reverse sm:flex-row justify-between items-center gap-2">
            <button
              onClick={() => onOpenChange(false)}
              className="text-gray-500 hover:text-gray-700 font-medium text-sm w-full sm:w-auto py-2 sm:py-0"
            >
              Maybe later
            </button>
            <button
              onClick={() => {
                onUpgrade();
                onOpenChange(false);
              }}
              className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm"
            >
              Upgrade to Pro
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PlanUpgradeModal;
